<template>
	<HeaderBar
		:service-title="t('title')"
		:navigation-items="navigationItems"
		:vuetify-options="headerBarOptions"
	>
		<div v-if="!$maintenanceEnabled && !$isMobile()">
			<div
				v-if="!logged"
				@click="connect"
			>
				<UserMenuBtn
					:full-name="t('connection')"
					:hide-logout-btn="true"
				/>
			</div>
			<UserMenuBtn
				v-else
				:full-name="name"
				:hide-logout-btn="false"
				@logout="onLogout"
			/>
		</div>
		<template
			v-if="$route.path.substring(0, 9) === '/register'"
			#navigation-bar-prepend
		>
			<VBtn
				text
				@click="$router.push('/')"
			>
				<VIcon class="mr-1">
					{{ mdiArrowLeft }}
				</VIcon>
				{{ t('back') }}
			</VBtn>
			<div class="vertical-separator" />
		</template>
		<template #navigation-bar-secondary-content>
			<div class="d-flex align-center">
				<VBtn
					text
					@click="$router.push('/faq')"
				>
					{{ t('help') }}
				</VBtn>
				<LangBtn
					v-model="lang"
					:available-languages="availableLanguages"
					:vuetify-options="{btn: {class: 'mx-6', color: 'white'}}"
				/>
			</div>
		</template>
		<template #navigation-drawer-prepend>
			<div
				v-if="!$maintenanceEnabled"
				class="w-100"
			>
				<VBtn
					v-if="logged"
					text
					disabled
				>
					<VIcon class="mr-2">
						{{ mdiAccount }}
					</VIcon>
					{{ user.firstname + ' ' + user.lastname }}
				</VBtn>
				<VBtn
					v-else
					color="white"
					outlined
					@click="connect"
				>
					<VIcon class="mr-2">
						{{ mdiAccount }}
					</VIcon>
					{{ t('connection') }}
				</VBtn>
				<div class="separator my-4" />
			</div>
		</template>
		<template #navigation-drawer-append>
			<div class="separator my-4" />
			<div class="d-flex justify-space-between w-100">
				<LangBtn
					v-model="lang"
					:available-languages="availableLanguages"
					:vuetify-options="{btn: {class: 'mr-2', width: '139px'}}"
				/>
				<VBtn
					v-if="logged"
					width="139px"
					color="white"
					outlined
					@click="onLogout"
				>
					{{ t('logout') }}
				</VBtn>
			</div>
		</template>
	</HeaderBar>
</template>

<script lang="ts">
	import Vue from 'vue';
	import Component, { mixins } from 'vue-class-component';
	import { mapState, mapActions } from 'vuex';
	import { NavigationItem } from '@cnamts/vue-dot/src/patterns/HeaderBar/types';
	import { mdiAccount, mdiArrowLeft } from '@mdi/js';
	import { i18n, loadLanguageAsync } from '@/i18n';
	import { Refs } from '@cnamts/vue-dot/src/types';

	const computed = Vue.extend({
		computed: {
			...mapState('appState', ['logged', 'user']),
			name(): string {
				return this.user.firstname + ' ' + this.user.lastname;
			},
			activeLink() {
				const fragments = this.$route.fullPath.split('/');
				const link = fragments[1] ?? '';
				return '/' + link;
			}
		}
	});

	const MixinsDeclaration = mixins(computed);

	@Component<AppHeader>({
		watch: {
			lang(val: string) {
				loadLanguageAsync(val);
			}
		},
		computed: {
			...mapState('appState', ['logged', 'user']),
			name(): string {
				return this.user.firstname + ' ' + this.user.lastname;
			},
			activeLink() {
				const fragments = this.$route.fullPath.split('/');
				const link = fragments[1] ?? '';
				return '/' + link;
			}
		},
		methods: {
			...mapActions('appState', ['login', 'logout'])
		}
	})
	export default class AppHeader extends MixinsDeclaration {
		$refs!: Refs<{
			headerBar: Vue & {
				toggleDrawer(): void;
			};
		}>;
		lang = i18n.locale;
		availableLanguages = ['fr', 'en', 'es'];
		mdiAccount = mdiAccount;
		mdiArrowLeft = mdiArrowLeft;

		headerBarOptions = {
			menuBtn: {
				btn: {
					color: 'primary',
					text: true
				}
			},
			navigationBar: {
				tab: this.activeLink
			}
		};

		mounted() {
			const token = sessionStorage.getItem('token');
			if (token) {
				this.login(token);
			}
		}

		t(path: string): string {
			return this.$t('components.layout.appHeader.' + path) as string;
		}

		connect(): void {
			if (this.$route.name !== 'login') {
				this.$router.push('/login');
			}
			if (this.$isMobile()) {
				this.$refs.headerBar?.toggleDrawer();
			}
		}

		onLogout(): void {
			this.logout();
			if (this.$isMobile()) {
				this.$refs.headerBar?.toggleDrawer();
			}
			if (this.$route.path !== '/') {
				this.$router.push('/');
			}
		}

		get navigationItems(): NavigationItem[] {
			if (this.$maintenanceEnabled) {
				return [];
			}
			if (this.$route.path.substring(0, 9) === '/register') {
				return [];
			}
			const tabs: NavigationItem[] = [];
			tabs.push({
				label: this.t('navigationItems.home'),
				to: '/'
			});
			if (this.logged) {
				if (this.$isMobile()) {
					tabs.push({
						label: this.$t('views.dashboard.dashboard.status') as string,
						to: '/dashboard/status'
					});
					tabs.push({
						label: this.$t('views.dashboard.dashboard.information') as string,
						to: '/dashboard/information'
					});
					tabs.push({
						label: this.$t('views.dashboard.dashboard.documents') as string,
						to: '/dashboard/documents'
					});
					tabs.push({
						label: this.$t('views.dashboard.dashboard.exchange') as string,
						to: '/dashboard/exchange'
					});
				} else {
					tabs.push({
						label: this.t('navigationItems.dashboard'),
						to: '/dashboard'
					});
				}
			}
			tabs.push({
				label: this.t('navigationItems.assistance'),
				to: '/assistance'
			});
			if (this.$isMobile()) {
				tabs.push({
					label: this.t('help'),
					to: '/faq'
				});
			}
			return tabs;
		}
	}
</script>

<style lang="scss" scoped>
	.vd-header-bar-container :deep(.vd-header-bar) {
		box-shadow: none !important;
	}
	:deep(.v-tab) {
		text-transform: none;
	}
	.separator {
		height: 1px;
		width: 100%;
		background-color: rgba(255, 255, 255, 0.8);
	}
	.vertical-separator {
		height: 24px;
		width: 1px;
		background-color: rgba(255, 255, 255, 0.8);
	}
</style>
